import { ancestorIds, buildTree, subtreeIds, visibleRows, type Tree, type TreeNode, type VisibleRow } from '../../schedule/lib/tree'
import type { CostNodeKind, CostRow } from '../types'

export { ancestorIds, buildTree, subtreeIds }
export type CostTree = Tree<CostRow>
export type CostTreeNode = TreeNode<CostRow>
export type BudgetRow = VisibleRow<CostRow>

/** Spec §1.1: at most four levels; groups at levels 1–3, items at levels 2–4. */
export const MAX_DEPTH = 4

export const normalizeSearch = (s: string): string => s.normalize('NFD').replace(/\p{Diacritic}/gu, '').toLowerCase().trim()

export interface RowFilter { rootId: string | null; search: string }

/**
 * Rows on screen: the chosen level-1 subtree (or everything), search matches on name or code together with
 * their ancestors, collapsed groups closed — except while searching, when every match is shown.
 */
export function budgetRows(tree: CostTree, collapsedIds: Set<string>, filter: RowFilter): BudgetRow[] {
  const q = normalizeSearch(filter.search)
  const inRoot = filter.rootId ? new Set(subtreeIds(tree, filter.rootId)) : null
  if (!inRoot && !q) return visibleRows(tree, collapsedIds)
  const matches = (n: CostTreeNode): boolean => {
    if (inRoot && !inRoot.has(n.activity.id)) return false
    if (!q) return true
    return normalizeSearch(n.activity.name).includes(q) || (!!n.activity.code && normalizeSearch(n.activity.code).includes(q))
  }
  return visibleRows(tree, q ? new Set<string>() : collapsedIds, matches)
}

/** Groups with children: what "collapse all" closes. */
export const collapsibleIds = (tree: CostTree): string[] => tree.order.filter((n) => n.children.length > 0).map((n) => n.activity.id)

/** Position among siblings in display order (the index moveCostNode expects). */
export const siblingPosition = (tree: CostTree, id: string): { index: number; count: number } => {
  const node = tree.byId.get(id)
  if (!node) return { index: -1, count: 0 }
  const siblings = node.parent ? node.parent.children : tree.roots
  return { index: siblings.indexOf(node), count: siblings.length }
}

export type PlacementProblem =
  | 'CostControl_error_item_at_root'
  | 'CostControl_error_parent_not_found'
  | 'CostControl_error_parent_not_group'
  | 'CostControl_error_move_into_itself'
  | 'CostControl_error_too_deep'

interface Shape { height: number; groupHeight: number }
const shapeOf = (node: CostTreeNode): Shape => {
  const kids = node.children.map(shapeOf)
  return {
    height: 1 + Math.max(0, ...kids.map((k) => k.height)),
    groupHeight: node.activity.kind === 'GROUP' ? 1 + Math.max(0, ...kids.map((k) => k.groupHeight)) : 0,
  }
}
/** `parentLevel`: 0 for the top level, else the parent's 1-based level. */
const fits = (parentLevel: number, shape: Shape) => parentLevel + shape.height <= MAX_DEPTH && parentLevel + shape.groupHeight <= MAX_DEPTH - 1

/**
 * Mirror of the server's placementError (src/services/costControl/tree.ts), so menus offer only what the
 * server accepts. `parentId` null = the top level.
 */
export function placementProblem(tree: CostTree, kind: CostNodeKind, parentId: string | null, movingId?: string): PlacementProblem | null {
  if (parentId === null && kind === 'ITEM') return 'CostControl_error_item_at_root'
  let parentLevel = 0
  if (parentId !== null) {
    const parent = tree.byId.get(parentId)
    if (!parent) return 'CostControl_error_parent_not_found'
    if (movingId && subtreeIds(tree, movingId).includes(parentId)) return 'CostControl_error_move_into_itself'
    if (parent.activity.kind !== 'GROUP') return 'CostControl_error_parent_not_group'
    parentLevel = parent.depth + 1
  }
  const moving = movingId ? tree.byId.get(movingId) : undefined
  const shape: Shape = moving ? shapeOf(moving) : { height: 1, groupHeight: kind === 'GROUP' ? 1 : 0 }
  return fits(parentLevel, shape) ? null : 'CostControl_error_too_deep'
}

/** A place "move to…" offers. `depth`: 0 = the top level, otherwise the target group's 1-based level. */
export interface MoveTarget { id: string | null; name: string; depth: number }

/** Where `id` may move: the top level (groups only) and every group placementProblem accepts, except its current parent. */
export function moveTargets(tree: CostTree, id: string): MoveTarget[] {
  const node = tree.byId.get(id)
  if (!node) return []
  const { kind, parentId } = node.activity
  const inside = new Set(subtreeIds(tree, id))
  const shape = shapeOf(node)
  const out: MoveTarget[] = []
  if (parentId !== null && kind === 'GROUP' && fits(0, shape)) out.push({ id: null, name: '', depth: 0 })
  for (const n of tree.order) {
    if (n.activity.kind !== 'GROUP' || n.activity.id === parentId || inside.has(n.activity.id)) continue
    if (fits(n.depth + 1, shape)) out.push({ id: n.activity.id, name: n.activity.name, depth: n.depth + 1 })
  }
  return out
}
